import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  detectLangFromPath,
  HTML_LANG,
  normalizeI18nLang,
  SUPPORTED_LANGS,
} from '../../utils/lang';

/**
 * Keep i18n.language and `<html lang>` in sync with the URL.
 *
 * The router is mounted with a per-language `basename`, so `useLocation()`
 * here returns paths *relative to that basename* (e.g. "/about", never
 * "/zh/about"). We instead read `window.location.pathname` for the absolute
 * URL because that's what carries the language signal.
 */
const LanguageSync: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const absolute = typeof window !== 'undefined' ? window.location.pathname : '/';
    const urlLang = detectLangFromPath(absolute);
    const currentLang = normalizeI18nLang(i18n.language);

    if (SUPPORTED_LANGS.includes(urlLang) && currentLang !== urlLang) {
      i18n.changeLanguage(urlLang);
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = HTML_LANG[urlLang];
    }
  }, [location.pathname, i18n]);

  return null;
};

export default LanguageSync;
